import { useQuery } from '@tanstack/react-query';
import { useContractQuery } from './useContractQuery';
import { useReadContracts } from './useContracts';

/* -------------------------------------------------------------- *
 *   Period state
 * -------------------------------------------------------------- */

/** Current period id (counts from 0). */
export function useCurrentPeriod() {
  return useContractQuery<bigint>(['crowdsale', 'getCurrentPeriod']);
}

/**
 * ETH raised in a given period (wei). Pass 0 for the live current period.
 *
 * Note: in MethaCrowdsale, getWeiRaisedPerPeriod(0) is the live aggregator
 * for the running period; positive integers refer to closed periods.
 */
export function usePeriodRaisedWei(periodId: number | bigint = 0) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getWeiRaisedPerPeriod', BigInt(periodId)],
    { refetchInterval: 12_000 }
  );
}

/** Average ETH raised across closed periods (wei). */
export function useAvgPeriodRaisedWei() {
  return useContractQuery<bigint>(['crowdsale', 'getAvgWeiRaisedPerPeriod']);
}

/** METH minted per period (constant in current contract — 287 on testnet). */
export function useTokensCreatedPerPeriod() {
  return useContractQuery<bigint>(['crowdsale', 'getTokensCreatedPerPeriod']);
}

/** Seconds remaining in current period before auto-close. Refetches every 5s. */
export function useRemainingPeriodTime() {
  return useContractQuery<bigint>(
    ['crowdsale', 'getRemainingPeriodTime'],
    { refetchInterval: 5_000, staleTime: 0 }
  );
}

/** [totalRaised, totalDistributed, ?] — aggregate stats. Tuple of 3 bigints. */
export function useTokensaleStats() {
  return useContractQuery<[bigint, bigint, bigint]>(['crowdsale', 'getTokensaleStats']);
}

/** Wei in the vault contract (treasury balance). */
export function useVaultBalance() {
  return useContractQuery<bigint>(['crowdsale', 'getContractBalance']);
}

/** Total wei ever raised across all periods. */
export function useTotalWeiRaised() {
  return useContractQuery<bigint>(['crowdsale', 'getWeiRaised']);
}

/** Period duration (seconds). 23h target on mainnet, 180s on testnet. */
export function usePeriodDuration() {
  return useContractQuery<bigint>(['crowdsale', 'getPeriodDuration']);
}

/** Min contribution (wei). */
export function useMinContribution() {
  return useContractQuery<bigint>(['crowdsale', 'getMinContribution']);
}

/** Last ERC-20 snapshot id taken at period close (used for governance vote weights). */
export function useLastSnapshotId() {
  return useContractQuery<bigint>(['crowdsale', 'getLastSnapshotId']);
}

/* -------------------------------------------------------------- *
 *   Account-aware queries
 * -------------------------------------------------------------- */

/** METH the user can claim from past closed periods (sum). */
export function useAccountClaimableTokens(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getAccountClaimableTokens', address ?? '0x0'],
    { enabled: !!address }
  );
}

/** ETH the user contributed in the live current period. */
export function useAccountCurrentPeriodContribution(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getAccountCurrentPeriodContribution', address ?? '0x0'],
    { enabled: !!address }
  );
}

/** Total wei the user has ever contributed across periods. */
export function useAccountTotalContribution(address: string | null | undefined) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getAccountTotalContribution', address ?? '0x0'],
    { enabled: !!address }
  );
}

/** Has this address ever participated in any period. */
export function useHasEverContributed(address: string | null | undefined) {
  return useContractQuery<boolean>(
    ['crowdsale', 'hasEverContributed', address ?? '0x0'],
    { enabled: !!address }
  );
}

/* -------------------------------------------------------------- *
 *   Period book / contributors list
 * -------------------------------------------------------------- */

/**
 * Number of contributors in a given period. Pass 0 for live current period.
 * Use together with useContributorsList to walk the period book.
 */
export function useContributorsLength(periodId: number | bigint = 0) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getContributorsLengthPerPeriod', BigInt(periodId)],
    { refetchInterval: 15_000 }
  );
}

/**
 * Tuple at (periodId, index) — (address, weiContribution, tokenBalance,
 * periodIndex, claimed). Use Number(idx) when iterating with a count.
 */
export function useContributorAt(periodId: number | bigint, index: number | bigint) {
  type Tuple = [string, bigint, bigint, bigint, boolean];
  return useContractQuery<Tuple>([
    'crowdsale',
    'getContributorsPeriodIndex',
    BigInt(periodId),
    BigInt(index),
  ]);
}

/* -------------------------------------------------------------- *
 *   Per-period account views (drive the granular claim panel)
 * -------------------------------------------------------------- */

/** ETH the user contributed in a specific (closed) period. */
export function useWeiContributionPerPeriod(
  periodId: number | bigint | null | undefined,
  address: string | null | undefined
) {
  return useContractQuery<bigint>(
    ['crowdsale', 'getWeiContributionPerPeriod', BigInt(periodId ?? 0), address ?? '0x0'],
    { enabled: !!address && periodId !== null && periodId !== undefined && Number(periodId) > 0 }
  );
}

/**
 * Enumerate every closed period the user contributed to but hasn't claimed
 * yet, newest first. Walks getAccountNextPeriodWithClaimableTokens by
 * threading the previous result back as the next fromPeriod, until it
 * returns 0 or we hit MAX_WALK.
 *
 * Cost: 1 RPC call per unclaimed period. With MAX_WALK = 256 the upper
 * bound is fine even on a public Infura/Sepolia endpoint. Most users will
 * have < 20 unclaimed periods so the typical cost is small.
 *
 * Pair with useWeiContributionPerPeriod(periodId, address) to render
 * per-row ETH amounts in the UI.
 */
const MAX_UNCLAIMED_WALK = 256;
export function useUnclaimedPeriods(address: string | null | undefined) {
  const contracts = useReadContracts();
  const { data: currentBn } = useCurrentPeriod();
  // Start the walk at currentPeriod + 1 — the contract's helper interprets it
  // as "find the highest closed period ≤ fromPeriod - 1 the user can claim".
  const startFrom = currentBn !== undefined ? Number(currentBn) + 1 : 0;

  return useQuery<number[], Error>({
    queryKey: ['metha', 'crowdsale', 'unclaimedPeriods', address ?? '0x0', startFrom] as const,
    queryFn: async () => {
      if (!address || startFrom <= 1) return [];
      const result: number[] = [];
      let from = BigInt(startFrom);
      for (let i = 0; i < MAX_UNCLAIMED_WALK; i++) {
        const nextBn = (await contracts.crowdsale.getAccountNextPeriodWithClaimableTokens(
          address, from,
        )) as bigint;
        const next = Number(nextBn);
        if (!next || next < 1) break;
        result.push(next);
        // Next walk window must look strictly below `next` — pass `next`
        // itself as fromPeriod and the contract walks fromPeriod-1 downward.
        from = BigInt(next);
        // Defensive break: the contract should already terminate at periodId
        // <= 1, but bound the loop locally too.
        if (next === 1) break;
      }
      return result; // already in newest-first order
    },
    enabled: !!address && startFrom > 1,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
